import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { titleCase } from "../../lib/format";
import type { CalendarEntry, CalendarMode } from "./calendarTypes";

export function calendarRange(mode: CalendarMode, anchor: Date) {
  if (mode === "month") {
    return {
      from: startOfWeek(startOfMonth(anchor), { weekStartsOn: 0 }),
      to: endOfWeek(endOfMonth(anchor), { weekStartsOn: 0 }),
    };
  }
  if (mode === "week" || mode === "portfolio") {
    return {
      from: startOfWeek(anchor, { weekStartsOn: 0 }),
      to: endOfWeek(anchor, { weekStartsOn: 0 }),
    };
  }
  if (mode === "day") return { from: startOfDay(anchor), to: endOfDay(anchor) };
  return { from: startOfDay(anchor), to: endOfDay(addDays(anchor, 30)) };
}

export function calendarViewTitle(mode: CalendarMode, anchor: Date, from: Date, to: Date) {
  if (mode === "month") return format(anchor, "MMMM yyyy");
  if (mode === "day") return format(anchor, "EEEE, MMMM d, yyyy");
  return `${format(from, "MMM d")} – ${format(to, "MMM d, yyyy")}`;
}

export function moveCalendarAnchor(mode: CalendarMode, anchor: Date, direction: -1 | 1) {
  if (mode === "month") return direction < 0 ? subMonths(anchor, 1) : addMonths(anchor, 1);
  if (mode === "week" || mode === "portfolio")
    return direction < 0 ? subWeeks(anchor, 1) : addWeeks(anchor, 1);
  if (mode === "day") return direction < 0 ? subDays(anchor, 1) : addDays(anchor, 1);
  return addDays(anchor, direction * 31);
}

export function localDateKey(day: Date): string {
  return format(day, "yyyy-MM-dd");
}

export function entryDateKey(entry: CalendarEntry): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: entry.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(entry.start));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function formatCalendarTime(value: string, timezone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

export function entryMarkers(entry: CalendarEntry): string[] {
  const markers: string[] = [];
  if (entry.conflictOverridden) markers.push("Conflict override");
  if (entry.overdueTaskCount)
    markers.push(
      `${entry.overdueTaskCount} overdue task${entry.overdueTaskCount === 1 ? "" : "s"}`,
    );
  else if (entry.openTaskCount)
    markers.push(`${entry.openTaskCount} open task${entry.openTaskCount === 1 ? "" : "s"}`);
  if (["overdue", "unpaid", "part_paid"].includes(entry.paymentState))
    markers.push(titleCase(entry.paymentState));
  if (entry.hasInventory) markers.push("Inventory");
  if (entry.hasSponsor) markers.push("Sponsor");
  return markers;
}

// entryPath is where a calendar row opens. The grid shows both kinds, because a room is occupied
// whoever is in it, but a hire and one of the client's own events are different records in
// different workspaces, and following the wrong one lands on a page that cannot exist.
export function entryPath(entry: CalendarEntry): string {
  return entry.kind === "event" ? `/events/${entry.eventId}` : `/bookings/${entry.bookingId}`;
}

function safeSpreadsheetValue(value: string): string {
  return /^[=+\-@]/u.test(value) ? `'${value}` : value;
}

function csvCell(value: string | number): string {
  const text = safeSpreadsheetValue(String(value));
  return `"${text.replaceAll('"', '""')}"`;
}

export function calendarCSV(entries: CalendarEntry[]): string {
  const header = [
    "Kind",
    "Reference",
    "Title",
    "Customer",
    "Starts",
    "Ends",
    "Timezone",
    "Venue",
    "Location",
    "Status",
    "Approval",
    "Payment",
    "Owner",
    "Source",
    "Needs attention",
  ];
  const rows = entries.map((entry) => [
    entry.kind === "event" ? "Event" : "Booking",
    entry.reference,
    entry.title,
    entry.customerName,
    entry.start,
    entry.end,
    entry.timezone,
    entry.venueNames.join("; "),
    entry.locationNames.join("; "),
    entry.status,
    entry.approvalState,
    entry.paymentState,
    entry.ownerName,
    entry.source,
    entryMarkers(entry).join("; "),
  ]);
  return `${[header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

export function calendarExportFilename(from: Date, to: Date): string {
  return `calendar-${format(from, "yyyy-MM-dd")}-to-${format(to, "yyyy-MM-dd")}.csv`;
}
